import { useState, type FormEvent } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import type { ClientConfig } from '@ferro/config';
import { useAuth } from '../contexts/AuthContext.js';
import { PasswordInput } from '../components/PasswordInput.js';
import { personaHomeRoute } from '../lib/route-permissions.js';

type LoginProps = {
  config: ClientConfig;
};

type LocationState = { from?: string } | null;

export function Login({ config }: LoginProps): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const fromState = (location.state as LocationState) ?? null;
  // Deep links win (state.from, preserved by ProtectedRoute); otherwise
  // the landing is role-aware (spec D3): persona roles (groomer/walker/
  // kennel_tech/accountant) land on their My Work home, desk roles
  // (owner/manager/receptionist) keep '/'.
  const from =
    fromState?.from && fromState.from !== '/login' ? fromState.from : null;
  // #459 — the invite-accept "Continue to sign in" link carries the just-set
  // email forward (`?email=`) so a brand-new staffer doesn't have to retype
  // the address they just typed on the previous screen.
  const [searchParams] = useSearchParams();
  const prefillEmail = searchParams.get('email') ?? '';
  const [username, setUsername] = useState(prefillEmail);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const result = await auth.login(config.deployment.apiUrl, username, password);
      if (result.error) {
        setError(result.error);
        return;
      }
      const home = personaHomeRoute(result.data?.staff.role) ?? '/';
      navigate(from ?? home, { replace: true });
    } catch (err) {
      // login() catches and wraps errors in ApiResponse; this is a defensive
      // safety net for unforeseen exceptions from useAuth itself.
      setError(err instanceof Error ? err.message : 'Unexpected error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="ferro-admin-login">
      <section className="ferro-auth">
        <h1 className="ferro-auth__title">Admin sign in</h1>
        <p className="ferro-auth__subtitle">
          {prefillEmail
            ? `Your password is set — sign in to ${config.brand.name}.`
            : `${config.brand.name} staff portal.`}
        </p>
        <form className="ferro-form" onSubmit={handleSubmit} noValidate>
          <label className="ferro-field" htmlFor="admin-login-username">
            <span className="ferro-field__label">Username</span>
            <input
              id="admin-login-username"
              type="text"
              name="username"
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              inputMode="email"
              required
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="ferro-field__input"
            />
          </label>
          <label className="ferro-field" htmlFor="admin-login-password">
            <span className="ferro-field__label">Password</span>
            <PasswordInput
              id="admin-login-password"
              name="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>
          {error ? (
            <p className="ferro-form__error" role="alert">
              {error}
            </p>
          ) : null}
          <button
            type="submit"
            className="ferro-btn ferro-btn--primary ferro-btn--block"
            disabled={submitting}
          >
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
        {/* #405 */}
        <p className="ferro-auth__hint">
          <a href="/staff/forgot-password">Forgot password?</a>
        </p>
      </section>
    </main>
  );
}
